/* global Set */
import _ from 'underscore';

/**
 * Compares two arrays of location targets and returns the differences.
 * If a location target has been modified (e.g. radius changed), the old will show as deleted and
 * the new change will show as added.
 *
 * Location targets will be compared by their hashcode so if user deletes a target and adds it back
 * with the same settings (i.e. the object reference has changed), it will be marked as no change.
 *
 * @param {LocationTarget[]} before - List of location targets fetched from server.
 * @param {LocationTarget[]} after - List of location targets after modified by user.
 *
 * @returns {Object} changes - Object containing the changes.
 * @returns {LocationTarget[]} changes.Added - List of location targets that have been added.
 * @returns {LocationTarget[]} changes.Deleted - List of location targets that have been deleted.
 * @returns {LocationTarget[]} changes.Updated - List of location targets that have been updated.
 */
export function compareLocationTargetArrays(before, after) {
  const changes = { Added: [], Deleted: [], Updated: [] };

  const updatedIds = new Set();

  // convert arrays into dictionaries, with hashcode as key
  const beforeDict = _.object(_.map(before, lt => [lt.getHashCode(), lt]));
  const afterDict = _.object(_.map(after, lt => [lt.getHashCode(), lt]));

  // calculate newly added location targets
  _.each(afterDict, (locationTarget, hashCode) => {
    if (!_.has(beforeDict, hashCode)) {
      if (_.isEmpty(locationTarget.Id)) {
        // no Id, this is a new location target
        changes.Added.push(locationTarget);
      } else {
        const originalTarget = _.findWhere(before, { Id: locationTarget.Id });

        if (originalTarget.isRadiusLocationTarget === locationTarget.isRadiusLocationTarget) {
          // target has been updated without converting type, mark it as an update
          updatedIds.add(locationTarget.Id);
          changes.Updated.push(locationTarget);
        } else {
          // target is converting from one type to the other, treat it as an add + delete operation
          changes.Added.push(locationTarget);
        }
      }
    }
  });

  // calculate deleted location targets
  _.each(beforeDict, (locationTarget, hashCode) => {
    if (!_.has(afterDict, hashCode) && !updatedIds.has(locationTarget.Id)) {
      changes.Deleted.push(locationTarget);
    }
  });

  return changes;
}
